'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';

import HeroSection from '../_components/HeroSection';
import Companies from '../_components/Companies';
import OurNews from '../_components/OurNews';
import ContactInformation from '../_components/ContactInformation';
import AboutUs from '../_components/AboutUs';

import OurValues from '../_components/OurValues';
import Objective from '../_components/Objective';
import Motion from '../_components/Motion';
import OurClients from '../_components/OurClients';
import facebookIcon from '@/assets/icons/facebook-icon.svg';
import linkedinIcon from '@/assets/icons/linkedin-icon.svg';
import tiktokIcon from '@/assets/icons/tiktok-icon.svg';
import instagramIcon from '@/assets/icons/instagram-icon.svg';
import xIcon from '@/assets/icons/x-icon.svg';

import { Header, Footer, LoadingSpinner } from '@/shared';
import { usePermissions } from '@/contexts/PermissionsContext';
import heroSectionPlanetMedia from '@/assets/imgs/hero-section-planet-media.svg';

export default function Home() {
  const { isSectionVisible, loading } = usePermissions();

  // Show loading state while permissions are being fetched
  if (loading) {
    return (
      <main className="overflow-x-hidden">
        <div className="flex min-h-screen items-center justify-center bg-black">
          <div className="flex flex-col items-center space-y-4">
            <LoadingSpinner size="xl" />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="overflow-x-hidden">
      <div className="relative max-h-[675px]">
        {/* Background Image */}
        <div className="absolute inset-0 flex justify-center">
          <Image
            src={heroSectionPlanetMedia}
            alt="hero section planet media"
            className="absolute bottom-0 z-10 w-full"
            loading="lazy"
          />

          {/* Social Media Icons */}
          <div
            className="absolute bottom-0 left-1/2 z-20 flex -translate-x-1/2 transform items-center gap-3 sm:gap-4 lg:bottom-10 lg:gap-4.5"
            role="group"
            aria-label="Social media links">
            <Link
              href="https://www.facebook.com/Eramgroup.Official/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow us on Facebook"
              className="text-white">
              <Image src={facebookIcon} alt="Facebook" className="size-5 sm:size-6 lg:size-7" />
            </Link>
            <Link
              href="https://www.linkedin.com/company/99168561/admin/dashboard/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow us on LinkedIn"
              className="text-white">
              <Image src={linkedinIcon} alt="LinkedIn" className="size-5 sm:size-6 lg:size-7" />
            </Link>
            <Link
              href="https://www.tiktok.com/@eram.group?lang=en"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow us on TikTok"
              className="text-white">
              <Image src={tiktokIcon} alt="TikTok" className="size-5 sm:size-6 lg:size-7" />
            </Link>
            <Link
              href="https://www.instagram.com/eram_group_sa/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow us on Instagram"
              className="text-white">
              <Image src={instagramIcon} alt="Instagram" className="size-5 sm:size-6 lg:size-7" />
            </Link>
            <Link
              href="https://x.com/eramgroup_sa"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow us on X"
              className="text-white">
              <Image src={xIcon} alt="X" className="size-5 sm:size-6 lg:size-7" />
            </Link>
          </div>
        </div>

        {/* Gradient Overlay */}
        <div
          className="max-w-laptop absolute inset-0 z-10 mx-auto h-[675px] w-full"
          style={{
            background:
              'linear-gradient(270deg, #000 0%, rgba(13, 3, 22, 0.15) 24.71%, rgba(13, 3, 22, 0.00) 50.18%, rgba(13, 3, 22, 0.15) 75.35%, #000 100%)',
          }}
          aria-hidden="true"
        />

        {/* Content Layer */}
        <div className="relative z-20">
          <Header />
        </div>

        <div className="px-4 lg:px-0">
          {isSectionVisible('home') && (
            <section id="home">
              <HeroSection />
            </section>
          )}
        </div>
      </div>

      {isSectionVisible('companies') && (
        <section id="companies">
          <Companies />
        </section>
      )}

      {isSectionVisible('aboutUs') && (
        <section id="about-us">
          <AboutUs />
        </section>
      )}

      {isSectionVisible('ourValues') && (
        <section id="our-values">
          <OurValues />
        </section>
      )}

      {isSectionVisible('objective') && (
        <section id="objective">
          <Objective />
        </section>
      )}

      {isSectionVisible('motion') && (
        <section id="motion">
          <Motion />
        </section>
      )}

      {isSectionVisible('news') && (
        <section id="news">
          <OurNews />
        </section>
      )}

      {isSectionVisible('ourClients') && (
        <section id="our-clients">
          <OurClients />
        </section>
      )}

      {isSectionVisible('contactUs') && (
        <section id="contact-us">
          <ContactInformation />
        </section>
      )}
      <Footer />
    </main>
  );
}
